import {
  Button,
  InputField,
  Label,
  Note,
  SingleSelect
} from "@appretio/ui-kit";
import { useState } from "react";
import {
  DIMENSION_VALUE_PATTERN,
  DIMENSION_VALUE_RULES,
  getFieldLabel
} from "../../forecastContext";
import { useContextFilters } from "../../hooks/manageCubes/useContextFilters";
import { useCubeContext } from "../../hooks/manageCubes/useCubeContext";
import { useManageCubesOperationToast } from "../../hooks/manageCubes/useManageCubesOperationToast";
import { ConfirmOperationDialog } from "./ConfirmOperationDialog";
import { CubeContextFilterGrid } from "./CubeContextFilterGrid";
import { targetsWholeDimension } from "./dimensionValueValidation";
import {
  buttonRow,
  errorText,
  fieldGroup,
  formColumn,
  hintText,
  narrowField,
  newValuesSection
} from "./manageCubesOperations.css";
import { useDimensionValueMappings } from "./useDimensionValueMappings";

/** The subset of the rename / merge mutation the form drives. */
interface DimMappingMutation {
  /** Fire the operation; the callbacks report the outcome. */
  mutate: (
    input: {
      cube_id: string;
      context: Record<string, string[]>;
      name_mapping: Record<string, string>;
    },
    options: {
      onSuccess: (data: { message: string }) => void;
      onError: () => void;
    }
  ) => void;
  /** True while the operation is in flight. */
  isPending: boolean;
}

/** Props for the shared Rename / Merge dimension-mapping form shell. */
interface DimMappingOperationFormProps {
  /** The target cube ID to operate on. */
  cubeId: string;
  /** The rename / merge mutation fired on confirm. */
  mutation: DimMappingMutation;
  /** Action verb — submit button, dialog confirm, and the no-selection error. */
  actionText: string;
  /** Confirm dialog title. */
  dialogTitle: string;
  /** Confirm dialog intro paragraph describing the operation's effect. */
  dialogBody: string;
  /** Heading above the dialog's mapping list ("Renames:" / "Merges:"). */
  mappingListTitle: string;
  /** Prompt above the pre-filled target value inputs. */
  mappingHint: string;
  /** Label for a dimension's target input, from the dimension's label. */
  fieldLabel: (dimLabel: string) => string;
  /**
   * Placeholder for a dimension's target input, from the dimension's label and
   * how many values are selected (a multi-selection may mean filter-only).
   */
  fieldPlaceholder: (dimLabel: string, sourceCount: number) => string;
  /** `_global` error shown when no pre-filled value was edited. */
  editPromptError: string;
  /** Offer mappings for multi-value selections too (merge). */
  allowMultiSource?: boolean;
  /** Hint above the context grid; defaults to the single-value instruction. */
  selectionHint?: string;
  /**
   * Per-field validation of a target value against the cube's stored values.
   * Consulted only when the mapping targets the whole dimension — with any
   * other dimension selected the backend guard decides on submit.
   */
  validateValue?: (
    existing: string[],
    selected: string,
    value: string
  ) => string | undefined;
  /**
   * Per-field check of the target against the dimension's own selection — runs
   * whatever else is selected, unlike {@link validateValue}, because it needs
   * no knowledge of stored combinations.
   */
  validateTarget?: (sources: string[], value: string) => string | undefined;
  /**
   * Offer the dimension's existing cube values in the target field, via the
   * creatable select: naming a target by hand invites a typo, and a typo is a
   * legal *new* value, so it would merge into nothing. A value that is not on
   * the list stays typeable — merging into a new name is a supported case.
   */
  suggestExistingValues?: boolean;
  /** Suffix for a dialog mapping row, from the stored values and the target. */
  mappingAnnotation?: (existing: string[], value: string) => string;
  /**
   * Warning shown in the confirm dialog, computed from the selections and the
   * dimensions being mapped — e.g. the intake-scope note for rename / merge.
   */
  dialogWarning?: (
    selections: Record<string, string[]>,
    mappedKeys: string[]
  ) => string | undefined;
}

export function DimMappingOperationForm({
  cubeId,
  mutation,
  actionText,
  dialogTitle,
  dialogBody,
  mappingListTitle,
  mappingHint,
  fieldLabel,
  fieldPlaceholder,
  editPromptError,
  allowMultiSource = false,
  selectionHint = "Select context elements. For target dimension(s), a " +
    "single value must be selected.",
  suggestExistingValues = false,
  validateValue,
  validateTarget,
  mappingAnnotation,
  dialogWarning
}: DimMappingOperationFormProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { showSuccess, ToastElement } = useManageCubesOperationToast();
  const { selections, activeFilters, handleFieldChange, resetSelections } =
    useContextFilters();
  const {
    newValues,
    setNewValues,
    errors,
    setErrors,
    mappingDims,
    editedMappings,
    hasValidMapping,
    resetMappings
  } = useDimensionValueMappings(selections, { allowMultiSource });

  const { data: cubeContext, isLoading: contextLoading } =
    useCubeContext(cubeId);

  const { data: filteredContext } = useCubeContext(cubeId, activeFilters);

  function fieldError(key: string, raw: string): string | undefined {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return undefined;
    if (!DIMENSION_VALUE_PATTERN.test(trimmed)) {
      return `Invalid ${getFieldLabel(key)} name. ${DIMENSION_VALUE_RULES}`;
    }
    const targetError = validateTarget?.(selections[key] ?? [], trimmed);
    if (targetError) return targetError;
    if (!targetsWholeDimension(selections, key)) return undefined;
    return validateValue?.(
      cubeContext?.options?.[key] ?? [],
      selections[key][0],
      trimmed
    );
  }

  function setTarget(key: string, value: string) {
    setNewValues((prev) => ({ ...prev, [key]: value }));
    setFieldError(key, fieldError(key, value));
  }

  function setFieldError(key: string, error: string | undefined) {
    setErrors((prev) => {
      const next = { ...prev };
      delete next["_global"];
      if (error) next[key] = error;
      else delete next[key];
      return next;
    });
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};

    if (mappingDims.length === 0) {
      errs["_global"] = allowMultiSource
        ? "At least one dimension must have a value selected to " +
          `${actionText.toLowerCase()}.`
        : "At least one dimension must have exactly one value selected to " +
          `${actionText.toLowerCase()}.`;
      setErrors(errs);
      return false;
    }

    if (editedMappings.length === 0) {
      errs["_global"] = editPromptError;
      setErrors(errs);
      return false;
    }

    for (const [key, val] of editedMappings) {
      const error = fieldError(key, val);
      if (error) {
        errs[key] = error;
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    setDialogOpen(true);
  }

  const warning = dialogWarning?.(
    selections,
    editedMappings.map(([key]) => key)
  );

  function handleConfirm() {
    const context: Record<string, string[]> = {};
    for (const [key, vals] of Object.entries(selections)) {
      if (vals.length > 0) context[key] = vals;
    }

    const nameMapping: Record<string, string> = {};
    for (const [key, val] of editedMappings) {
      nameMapping[key] = val.trim();
    }

    mutation.mutate(
      { cube_id: cubeId, context, name_mapping: nameMapping },
      {
        onSuccess: (data) => {
          showSuccess(data.message);
          resetSelections();
          resetMappings();
          setDialogOpen(false);
        },
        // The global mutation handler already toasts the failure with the
        // server's reason — a local toast would duplicate it.
        onError: () => {
          setDialogOpen(false);
        }
      }
    );
  }

  return (
    <>
      {ToastElement}
      <div className={formColumn}>
        <p className={hintText}>{selectionHint}</p>
        <CubeContextFilterGrid
          options={cubeContext?.options}
          filteredOptions={filteredContext?.options}
          selections={selections}
          hasActiveFilters={!!activeFilters}
          isLoading={contextLoading}
          setSelected={handleFieldChange}
        />

        {mappingDims.length > 0 && (
          <div className={newValuesSection}>
            <p className={hintText}>{mappingHint}</p>
            {mappingDims.map(([key]) => (
              <div key={key} className={`${fieldGroup} ${narrowField}`}>
                <Label label={fieldLabel(getFieldLabel(key))} />
                {suggestExistingValues ? (
                  <SingleSelect
                    options={(cubeContext?.options?.[key] ?? []).map(
                      (option) => ({ label: option, value: option })
                    )}
                    // A typed target that is not (yet) a cube value is still
                    // the selection, so it survives a re-render of the list.
                    selectedOption={
                      newValues[key]
                        ? { label: newValues[key], value: newValues[key] }
                        : undefined
                    }
                    placeholder={fieldPlaceholder(
                      getFieldLabel(key),
                      selections[key]?.length ?? 0
                    )}
                    onChange={(value) => setTarget(key, value ?? "")}
                    allowCreate
                    onCreate={(value) => setTarget(key, value)}
                    formatCreateLabel={(value) =>
                      `Use "${value}" — new value, behaves like a rename`
                    }
                    error={errors[key]}
                    size="sm"
                  />
                ) : (
                  <InputField
                    placeholder={fieldPlaceholder(
                      getFieldLabel(key),
                      selections[key]?.length ?? 0
                    )}
                    value={newValues[key] ?? ""}
                    onChange={(e) => setTarget(key, e.target.value)}
                    error={errors[key]}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {errors["_global"] && <p className={errorText}>{errors["_global"]}</p>}

        <div className={buttonRow}>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!hasValidMapping}
          >
            {actionText}
          </Button>
        </div>
      </div>
      <ConfirmOperationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={dialogTitle}
        confirmText={actionText}
        onConfirm={handleConfirm}
        isLoading={mutation.isPending}
      >
        <p>{dialogBody}</p>
        <p>
          <strong>Context:</strong>
        </p>
        <ul>
          {Object.entries(selections)
            .filter(([, vals]) => vals.length > 0)
            .map(([key, vals]) => (
              <li key={key}>
                <strong>{getFieldLabel(key)}:</strong> {vals.join(", ")}
              </li>
            ))}
        </ul>
        <p>
          <strong>{mappingListTitle}</strong>
        </p>
        <ul>
          {editedMappings.map(([key, val]) => (
            <li key={key}>
              <strong>{getFieldLabel(key)}:</strong>{" "}
              {selections[key].join(", ")} → {val.trim()}
              {mappingAnnotation &&
                ` ${mappingAnnotation(cubeContext?.options?.[key] ?? [], val)}`}
            </li>
          ))}
        </ul>
        {warning && <Note variant="warning" size="compact" text={warning} />}
      </ConfirmOperationDialog>
    </>
  );
}
